import type { Repo } from '../data'
import type { SnapshotPlayer } from '../data/snapshotTypes'
import type { ID, Player } from '../domain/types'

/**
 * Bring the local player table in line with the synced roster.
 *
 * Amendment 03.1(2): a synced player uses THE SERVER'S id, taken from
 * `roster[].id`, rather than one minted here. Previously the PWA generated its
 * own uuid and matched on playhqProfileId, which meant every stat event would
 * have needed translating on its way up to a `stat_events` row -- a mapping
 * layer that gets more expensive the longer it is deferred, and impossible to
 * get right once the same event exists on two devices.
 *
 * Matching is still on playhqProfileId, never on name, which changes whenever an
 * admin renames someone. Locally added fill-ins keep client-generated ids: they
 * have no server row to borrow an id from.
 *
 * Players added by hand on the iPad (fill-ins, no profile id) are left alone.
 *
 * Concurrency: two overlapping runs would each read an empty table and each
 * create a full set of players, producing exact duplicates. React StrictMode
 * does precisely that in development by invoking effects twice. Runs are
 * serialised below, and any duplicates already written are healed on the next
 * pass.
 */

let inFlight: Promise<ReconcileResult> | null = null

export interface ReconcileResult {
  players: Player[]
  created: number
  updated: number
  /** Duplicate rows removed by the healing pass. */
  deduped: number
  /** Players moved from a locally minted id onto the server's id (03.1(2)). */
  rekeyed: number
}

export function reconcilePlayers(
  repo: Repo,
  existing: Player[],
  roster: SnapshotPlayer[],
): Promise<ReconcileResult> {
  // Serialise: a second caller waits for the first rather than racing it.
  const run = (inFlight ?? Promise.resolve()).then(
    () => reconcileOnce(repo, roster),
    () => reconcileOnce(repo, roster),
  )
  inFlight = run
  void run.catch(() => {}).then(() => {
    if (inFlight === run) inFlight = null
  })
  void existing // storage is the authority; the caller's copy may be stale
  return run
}

async function reconcileOnce(repo: Repo, roster: SnapshotPlayer[]): Promise<ReconcileResult> {
  // Always read inside the critical section, never trust a snapshot of state
  // taken before we were queued.
  const current = await repo.listPlayers()

  const { players: healed, deduped } = await healDuplicates(repo, current)
  // One-off: adopt the server id for anyone still on a locally minted one.
  const { players: rekeyed, rekeyedCount } = await adoptServerIds(repo, healed, roster)

  const byProfile = new Map<string, Player>()
  for (const p of rekeyed) {
    if (p.playhqProfileId) byProfile.set(p.playhqProfileId, p)
  }

  const now = Date.now()
  const next = [...rekeyed]
  let created = 0
  let updated = 0

  for (const entry of roster) {
    // Someone PlayHQ never named has no cross-game identity, so they are never a
    // roster member -- they are recorded as a fill-in appearance server-side.
    if (!entry.playhq_profile_id) continue

    const match = byProfile.get(entry.playhq_profile_id)
    if (!match) {
      const player: Player = {
        // The server's id, so a stat event needs no translation later.
        id: entry.id,
        name: entry.display_name,
        active: !entry.archived,
        playhqProfileId: entry.playhq_profile_id,
        isFillIn: entry.is_fill_in,
        needsNaming: entry.needs_naming,
        jerseyNumber: entry.jersey_number,
        createdAt: now,
        updatedAt: now,
      }
      await repo.savePlayer(player)
      byProfile.set(entry.playhq_profile_id, player)
      next.push(player)
      created++
      continue
    }

    // Only write when something actually changed -- avoids churning updatedAt on
    // every app open, which would matter once this syncs upward.
    const changed =
      match.name !== entry.display_name ||
      match.active !== !entry.archived ||
      (match.jerseyNumber ?? null) !== entry.jersey_number ||
      (match.needsNaming ?? false) !== entry.needs_naming ||
      (match.isFillIn ?? false) !== entry.is_fill_in

    if (changed) {
      const merged: Player = {
        ...match,
        name: entry.display_name,
        active: !entry.archived,
        jerseyNumber: entry.jersey_number,
        needsNaming: entry.needs_naming,
        isFillIn: entry.is_fill_in,
        updatedAt: now,
      }
      await repo.savePlayer(merged)
      byProfile.set(entry.playhq_profile_id, merged)
      const i = next.findIndex((p) => p.id === match.id)
      if (i >= 0) next[i] = merged
      updated++
    }
  }

  return { players: next, created, updated, deduped, rekeyed: rekeyedCount }
}

/**
 * Move any player still carrying a locally minted id onto the server's id,
 * carrying their stat events across.
 *
 * Runs once per player and then never fires again. It is written to be safe to
 * interrupt: events are repointed before the old player row is removed, so a
 * failure half way leaves duplicated players rather than orphaned events. Losing
 * an event is the only outcome that actually matters.
 */
async function adoptServerIds(
  repo: Repo,
  players: Player[],
  roster: SnapshotPlayer[],
): Promise<{ players: Player[]; rekeyedCount: number }> {
  const serverIdByProfile = new Map<string, ID>()
  for (const entry of roster) {
    if (entry.playhq_profile_id) serverIdByProfile.set(entry.playhq_profile_id, entry.id)
  }

  const wrong = players.filter(
    (p) => p.playhqProfileId && serverIdByProfile.get(p.playhqProfileId) && serverIdByProfile.get(p.playhqProfileId) !== p.id,
  )
  if (wrong.length === 0) return { players, rekeyedCount: 0 }

  const allEvents = await repo.listAllEvents()
  const next = [...players]

  for (const old of wrong) {
    const serverId = serverIdByProfile.get(old.playhqProfileId!)!
    // If the server id is somehow already present, keep that row and fold into it.
    const existing = players.find((p) => p.id === serverId)

    const moved = allEvents
      .filter((e) => e.playerId === old.id)
      .map((e) => ({ ...e, playerId: serverId, updatedAt: Date.now() }))
    if (moved.length > 0) await repo.saveEvents(moved)

    if (!existing) {
      await repo.savePlayer({ ...old, id: serverId, updatedAt: Date.now() })
      const i = next.findIndex((p) => p.id === old.id)
      if (i >= 0) next[i] = { ...old, id: serverId }
    } else {
      const i = next.findIndex((p) => p.id === old.id)
      if (i >= 0) next.splice(i, 1)
    }
    await repo.deletePlayer(old.id)

    // Games reference players by id in their roster array; repoint those too.
    const games = await repo.listGames()
    for (const g of games) {
      if (!g.roster.includes(old.id)) continue
      const roster2 = [...new Set(g.roster.map((id) => (id === old.id ? serverId : id)))]
      await repo.saveGame({ ...g, roster: roster2, updatedAt: Date.now() })
    }
  }

  return { players: next, rekeyedCount: wrong.length }
}

/**
 * Remove duplicate rows sharing a PlayHQ profile id, keeping the earliest.
 *
 * A duplicate that already has stat events attached is kept and archived rather
 * than deleted: losing an event is far worse than showing a stale name, and a
 * human can merge it later. In practice duplicates come from the double-boot
 * bug and carry no events at all.
 */
async function healDuplicates(repo: Repo, players: Player[]): Promise<{ players: Player[]; deduped: number }> {
  const groups = new Map<string, Player[]>()
  for (const p of players) {
    // Fill-ins have no profile id and are per-game, so they are never roster
    // members and never duplicates of one. Skipped explicitly, not incidentally.
    if (p.isFillIn || !p.playhqProfileId) continue
    const list = groups.get(p.playhqProfileId)
    if (list) list.push(p)
    else groups.set(p.playhqProfileId, [p])
  }

  const duplicated = [...groups.values()].filter((g) => g.length > 1)
  if (duplicated.length === 0) return { players, deduped: 0 }

  const events = await repo.listAllEvents()
  const playerIdsWithEvents = new Set<ID>(events.filter((e) => !e.deleted).map((e) => e.playerId))

  const removed = new Set<ID>()
  for (const group of duplicated) {
    const ordered = group.slice().sort((a, b) => a.createdAt - b.createdAt || a.id.localeCompare(b.id))
    const keep = ordered.find((p) => playerIdsWithEvents.has(p.id)) ?? ordered[0]!
    for (const p of ordered) {
      if (p.id === keep.id) continue
      if (playerIdsWithEvents.has(p.id)) {
        // Has history: archive rather than destroy it.
        await repo.savePlayer({ ...p, active: false, updatedAt: Date.now() })
      } else {
        await repo.deletePlayer(p.id)
        removed.add(p.id)
      }
    }
  }

  return { players: players.filter((p) => !removed.has(p.id)), deduped: removed.size }
}
